/**
 * Real-time notification socket for the vendor portal (PR-ζ₅).
 *
 * Behaviour:
 *   - Opens `wss://<api>/api/v1/vendor/ws?token=<JWT>` after we have an
 *     access token.
 *   - On `event === "notification"` invalidates the
 *     `['notifications']` query so the bell-icon counter refreshes,
 *     and dispatches a global `vendoriq:notification` CustomEvent so
 *     other components (toasts, in-page banners) can subscribe.
 *   - Reconnects with exponential backoff up to 30s when the server
 *     drops the connection. We do NOT reconnect on auth errors
 *     (close code 4401) — the user simply needs to re-login.
 *   - Clean-up on unmount or token change.
 *
 * Why a single hook in App.tsx and not per-page hooks: notifications
 * should fan out across the whole portal, not just whichever page is
 * mounted. A dedicated provider component (`VendorRealtimeBridge`)
 * mounts once at the protected-route boundary.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '../api/client';
import { realtimeApi } from '../api/realtime';

export interface RealtimeNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  category: string;
  priority: 'low' | 'medium' | 'high';
  related_entity_type: string | null;
  related_entity_id: string | null;
  data: Record<string, unknown>;
  created_at: string | null;
}

const RECONNECT_INITIAL_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

function buildWsBase(): string | null {
  // Same host as the REST API but ws:// scheme. We support both
  // VITE_API_URL = absolute "https://api.vendoriq.app" and the
  // relative-default "/api/v1". For the relative case, derive from
  // window.location.
  const apiBase = import.meta.env.VITE_API_URL || '/api/v1';
  let httpBase: string;
  try {
    if (apiBase.startsWith('http')) {
      httpBase = apiBase;
    } else {
      httpBase = `${window.location.origin}${apiBase}`;
    }
  } catch {
    return null;
  }
  // Strip trailing slash + any v1 suffix to construct the WS path.
  const base = httpBase.replace(/\/+$/, '');
  // Build the absolute /api/v1/vendor/ws path
  let withVendor: string;
  if (base.endsWith('/api/v1')) {
    withVendor = `${base}/vendor/ws`;
  } else if (base.endsWith('/v1')) {
    withVendor = `${base}/vendor/ws`;
  } else {
    withVendor = `${base}/api/v1/vendor/ws`;
  }
  return withVendor.replace(/^http/, 'ws');
}

export function useVendorWebSocket(enabled: boolean): void {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectDelayRef = useRef<number>(RECONNECT_INITIAL_MS);
  const stoppedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    stoppedRef.current = false;

    const connect = async () => {
      const token = getAccessToken();
      if (!token) {
        // Not logged in yet — try again in a moment.
        scheduleReconnect();
        return;
      }
      const wsBase = buildWsBase();
      if (!wsBase) return;

      // Prefer a short-lived single-use ticket so the long-lived JWT stays
      // out of the ws URL (logs/history). Fall back to ?token= when the
      // ticket endpoint is unavailable (Redis blip → 503) so realtime never
      // hard-breaks. A stale token/session cancels the attempt.
      let url: string;
      const ticket = await realtimeApi.wsTicket();
      if (stoppedRef.current) return;
      if (ticket) {
        url = `${wsBase}?ticket=${encodeURIComponent(ticket)}`;
      } else {
        // Re-read the token: it may have rotated during the async mint.
        const freshToken = getAccessToken();
        if (!freshToken) {
          scheduleReconnect();
          return;
        }
        url = `${wsBase}?token=${encodeURIComponent(freshToken)}`;
      }

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (err) {
        console.warn('[realtime] WebSocket constructor failed', err);
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectDelayRef.current = RECONNECT_INITIAL_MS;
      };

      ws.onmessage = (e) => {
        let parsed: { event?: string; data?: RealtimeNotification | Record<string, unknown>; type?: string };
        try {
          parsed = JSON.parse(e.data);
        } catch {
          return;
        }
        // Wave-75: real-time message push from admin → vendor. Server
        // sends {"type": "vendor.message.new", conversation_id, preview,
        // sender, ts}. Invalidate messages queries so the badge ticks
        // immediately + the open thread refreshes.
        if ((parsed as any).type === 'vendor.message.new') {
          qc.invalidateQueries({ queryKey: ['messages'] });
          try {
            window.dispatchEvent(
              new CustomEvent('vendoriq:message-new', { detail: parsed }),
            );
          } catch {
            // ignore — old browsers
          }
          return;
        }
        if (parsed.event === 'notification' && parsed.data) {
          // Refresh the notifications list + unread counter.
          qc.invalidateQueries({ queryKey: ['notifications'] });
          qc.invalidateQueries({ queryKey: ['notifications', 'unread'] });

          // Cross-component announce so toast components can pick it up.
          try {
            window.dispatchEvent(
              new CustomEvent<RealtimeNotification>('vendoriq:notification', {
                detail: parsed.data as RealtimeNotification,
              })
            );
          } catch {
            // ignore — old browsers
          }

          // Cache invalidation for related entity lists so the affected
          // page re-fetches automatically.
          const n = parsed.data as RealtimeNotification;
          if (n.related_entity_type === 'order') {
            qc.invalidateQueries({ queryKey: ['orders'] });
          } else if (n.related_entity_type === 'review') {
            qc.invalidateQueries({ queryKey: ['reviews'] });
          } else if (n.related_entity_type === 'return') {
            qc.invalidateQueries({ queryKey: ['returns'] });
          } else if (n.related_entity_type === 'payout') {
            qc.invalidateQueries({ queryKey: ['payouts'] });
          }
        }
      };

      ws.onclose = (e) => {
        wsRef.current = null;
        if (e.code === 4401) {
          // Auth failure — don't loop. User must log in again.
          stoppedRef.current = true;
          return;
        }
        if (!stoppedRef.current) {
          scheduleReconnect();
        }
      };

      ws.onerror = () => {
        // Let onclose handle the reconnect; we just log.
        // (Errors fire before close in many cases.)
      };
    };

    const scheduleReconnect = () => {
      if (stoppedRef.current) return;
      if (reconnectTimeoutRef.current) return;
      const delay = reconnectDelayRef.current;
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        reconnectDelayRef.current = Math.min(delay * 2, RECONNECT_MAX_MS);
        connect();
      }, delay);
    };

    connect();

    // Re-open the socket whenever the access token rotates (silent
    // refresh path). Without this, the existing socket holds the
    // pre-rotation token until the server's per-tick sweep closes it
    // — same effect, slower.
    const onTokenRotated = () => {
      if (stoppedRef.current) return;
      const ws = wsRef.current;
      if (ws) {
        try {
          ws.close();  // triggers onclose → scheduleReconnect
        } catch {
          // ignore
        }
      } else {
        // No live socket — schedule an immediate reconnect attempt.
        reconnectDelayRef.current = RECONNECT_INITIAL_MS;
        scheduleReconnect();
      }
    };
    window.addEventListener('vendoriq:token-rotated', onTokenRotated);

    return () => {
      stoppedRef.current = true;
      window.removeEventListener('vendoriq:token-rotated', onTokenRotated);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.close();
        } catch {
          // ignore
        }
        wsRef.current = null;
      }
    };
  }, [enabled, qc]);
}
